import React, { useState, useEffect } from 'react';
import { 
  Layout, 
  Card, 
  Table, 
  Button, 
  Input, 
  Space, 
  Modal, 
  Form, 
  Select, 
  Switch, 
  Tag, 
  Typography, 
  Row, 
  Col, 
  message, 
  Popconfirm,
  Tooltip,
  Avatar,
  Divider,
  Tabs,
  List,
  Badge,
  Image
} from 'antd';
import { 
  PlusOutlined, 
  SearchOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  SaveOutlined,
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  MessageOutlined,
  InboxOutlined,
  TeamOutlined,
  ExclamationCircleOutlined,
  ShoppingOutlined,
  EyeOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Navigation from '../components/Navigation';
import TicketList from '../components/TicketList';
import TicketDetail from '../components/TicketDetail';
import ClientCard from '../components/ClientCard';
import ProductCard from '../components/ProductCard';

const { Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const Clients = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('messages');
  const [form] = Form.useForm();
  const [newTicketsCount, setNewTicketsCount] = useState(0);
  const [isProductsModalVisible, setIsProductsModalVisible] = useState(false);
  const [clientProducts, setClientProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [selectedClientForProducts, setSelectedClientForProducts] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const token = localStorage.getItem('token');

  useEffect(() => {
    if (/^\/clients\/\d+/.test(location.pathname) || /^\/products\/\d+/.test(location.pathname)) {
      return;
    }
    if (!token) {
      navigate('/login');
      return;
    }
    loadClients();
    loadNewTicketsCount();
  }, [token, navigate, location.pathname]);

  // Перезагружаем клиентов при изменении фильтров
  useEffect(() => {
    if (/^\/clients\/\d+/.test(location.pathname) || /^\/products\/\d+/.test(location.pathname)) {
      return;
    }
    if (token) {
      loadClients();
    }
  }, [searchText, selectedStatus, location.pathname]);

  // Устанавливаем значения формы при открытии модального окна с клиентом
  useEffect(() => {
    if (editingClient && isModalVisible) {
      console.log('Устанавливаем значения формы для клиента:', editingClient);
      form.setFieldsValue({
        first_name: editingClient.first_name || '',
        last_name: editingClient.last_name || '',
        email: editingClient.email || '',
        phone: editingClient.phone || '',
        telegram_id: editingClient.telegram_id || '',
        telegram_nickname: editingClient.telegram_nickname || '',
        is_active: editingClient.is_active !== undefined ? editingClient.is_active : true,
        comment: editingClient.comment || ''
      });
    } else if (!editingClient && isModalVisible) {
      // Сбрасываем форму для нового клиента
      form.resetFields();
    }
  }, [editingClient, isModalVisible, form]);

  const loadClients = async () => {
    setLoading(true);
    try {
      const response = await axios.get('https://limitverify.ru:5000/api/clients', {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          search: searchText,
          status: selectedStatus !== 'all' ? selectedStatus : '',
          page: 1,
          limit: 100
        }
      });
      
      setClients(response.data.clients || []);
    } catch (error) {
      console.error('Ошибка загрузки клиентов:', error);
      if (error.response?.status === 403) {
        message.error('У вас нет прав для просмотра клиентов');
        navigate('/forbidden');
      } else {
        message.error('Ошибка загрузки клиентов');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadNewTicketsCount = async () => {
    try {
      const response = await axios.get('https://limitverify.ru:5000/api/tickets/stats', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setNewTicketsCount(response.data.stats.new || 0);
      }
    } catch (error) {
      console.error('Ошибка загрузки счетчика тикетов:', error);
    }
  };

  const handleSearch = (value) => {
    setSearchText(value);
  };

  const handleStatusChange = (value) => {
    setSelectedStatus(value);
  };

  const filteredClients = clients.filter(client => {
    const matchesSearch = 
      client.first_name?.toLowerCase().includes(searchText.toLowerCase()) ||
      client.last_name?.toLowerCase().includes(searchText.toLowerCase()) ||
      client.full_name?.toLowerCase().includes(searchText.toLowerCase()) ||
      client.email?.toLowerCase().includes(searchText.toLowerCase()) ||
      client.phone?.includes(searchText) ||
      client.telegram_nickname?.toLowerCase().includes(searchText.toLowerCase());
    
    const matchesStatus = selectedStatus === 'all' || client.is_active === (selectedStatus === 'active');
    
    return matchesSearch && matchesStatus;
  });

  const showClientModal = (client = null) => {
    setEditingClient(client);
    // Всегда открываем в режиме редактирования
    setIsEditing(true);
    if (client) {
      console.log('showClientModal: данные клиента:', client);
      form.setFieldsValue({
        first_name: client.first_name || '',
        last_name: client.last_name || '',
        email: client.email || '',
        phone: client.phone || '',
        telegram_id: client.telegram_id || '',
        telegram_nickname: client.telegram_nickname || '',
        is_active: client.is_active !== undefined ? client.is_active : true,
        comment: client.comment || ''
      });
    } else {
      form.resetFields();
    }
    setIsModalVisible(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      
      if (editingClient) {
        // Обновление существующего клиента
        await axios.put(`https://limitverify.ru:5000/api/clients/${editingClient.id}`, values, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Обновляем локальное состояние
        const updatedClients = clients.map(c => 
          c.id === editingClient.id ? { ...c, ...values } : c
        );
        setClients(updatedClients);
        message.success('Клиент обновлен успешно!');
      } else {
        // Создание нового клиента
        const response = await axios.post('https://limitverify.ru:5000/api/clients', values, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const newClient = response.data.client;
        setClients([...clients, newClient]);
        message.success('Клиент создан успешно!');
      }

      setIsModalVisible(false);
      setIsEditing(false);
      setEditingClient(null);
    } catch (error) {
      console.error('Ошибка сохранения:', error);
      if (error.response?.status === 403) {
        message.error('У вас нет прав для изменения клиентов');
      } else {
        const apiError = error.response?.data?.error;
        const known = {
          'Client with this email already exists': 'Клиент с таким email уже существует',
          'First name, last name and email are required': 'Укажите имя, фамилию и email',
          'Agreement with offer and privacy policy is required': 'Необходимо согласие с офертой и политикой'
        };
        message.error(known[apiError] || apiError || 'Ошибка при сохранении клиента');
      }
    }
  };

  const checkClientHasProducts = async (clientId) => {
    try {
      const response = await axios.get(`https://limitverify.ru:5000/api/clients/${clientId}/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data.products && response.data.products.length > 0;
    } catch (error) {
      console.error('Ошибка проверки изделий клиента:', error);
      return false;
    }
  };

  const showClientProductsModal = async (client) => {
    setSelectedClientForProducts(client);
    setIsProductsModalVisible(true);
    await loadClientProducts(client.id);
  };

  const loadClientProducts = async (clientId) => {
    setProductsLoading(true);
    try {
      // Запрашиваем все товары (активные и архивные)
      const response = await axios.get(`https://limitverify.ru:5000/api/product-history/client/${clientId}/products?status=all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Response from API:', response.data);
      
      if (response.data.success && response.data.data) {
        // Преобразуем данные из формата ProductOwner в формат для отображения
        const products = response.data.data.map(item => {
          const p = item.product || {};
          const photos = p.photos || [];
          const primary = photos.find((ph) => ph.isPrimary) || photos[0];
          return {
          id: p.id || item.id,
          name: p.name || '',
          serialNumber: p.serialNumber || '',
          identificationNumber: p.identificationNumber || '',
          photoUrl: primary?.photoUrl || primary?.originalPhotoUrl || p.photoUrl || p.originalPhotoUrl || null,
          status: item.status === 'current' ? 'АКТИВНО' : 'АРХИВ',
          ownershipStartDate: item.ownershipStartDate || item.ownership_start_date,
          ownershipEndDate: item.ownershipEndDate || item.ownership_end_date,
          size: p.size?.name || '',
          color: p.color?.name || '',
          material: p.material?.name || ''
          };
        });
        console.log('Processed products:', products);
        setClientProducts(products);
      } else {
        console.log('No products found or invalid response format');
        setClientProducts([]);
      }
    } catch (error) {
      console.error('Ошибка загрузки товаров клиента:', error);
      console.error('Error details:', error.response?.data);
      message.error('Ошибка загрузки товаров клиента');
      setClientProducts([]);
    } finally {
      setProductsLoading(false);
    }
  };

  const handleDelete = async (clientId) => {
    try {
      // Проверяем, есть ли у клиента изделия
      const hasProducts = await checkClientHasProducts(clientId);
      
      if (hasProducts) {
        message.error('Нельзя удалить клиента, у которого есть зарегистрированные изделия!');
        return;
      }

      await axios.delete(`https://limitverify.ru:5000/api/clients/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const updatedClients = clients.filter(c => c.id !== clientId);
      setClients(updatedClients);
      message.success('Клиент удален успешно!');
    } catch (error) {
      console.error('Ошибка удаления:', error);
      if (error.response?.status === 403) {
        message.error('У вас нет прав для удаления клиентов');
      } else {
        message.error('Ошибка удаления клиента');
      }
    }
  };

  // Компонент для вкладки CRM (тикеты)
  const MessagesTab = () => {
    const path = window.location.pathname;
    
    if (path.includes('/tickets/') && path.split('/tickets/')[1]) {
      return <TicketDetail />;
    }
    
    return <TicketList />;
  };

  const columns = [
    {
      title: 'Клиент',
      dataIndex: 'full_name',
      key: 'full_name',
      render: (fullName, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Avatar 
            size={48} 
            icon={<UserOutlined />}
            style={{ backgroundColor: record.is_active ? '#52c41a' : '#d9d9d9' }}
          />
          <div>
            <div style={{ fontWeight: 500, fontSize: '14px' }}>
              {record.first_name} {record.last_name}
            </div>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {record.email}
            </Text>
          </div>
        </div>
      )
    },
    {
      title: 'Контакты',
      key: 'contacts',
      render: (_, record) => (
        <div>
          {record.phone && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              <PhoneOutlined style={{ color: '#1890ff' }} />
              <Text>{record.phone}</Text>
            </div>
          )}
          {record.telegram_nickname && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <MessageOutlined style={{ color: '#1890ff' }} />
              <Text>@{record.telegram_nickname}</Text>
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Статус',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Активен' : 'Неактивен'}
        </Tag>
      )
    },
    {
      title: 'Дата регистрации',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: (date) => (
        <Text type="secondary">
          {new Date(date).toLocaleDateString('ru-RU')}
        </Text>
      )
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Карточка клиента">
            <Button 
              type="text" 
              icon={<EyeOutlined />} 
              onClick={() => navigate(`/clients/${record.id}`)}
              size="small"
            />
          </Tooltip>
          <Tooltip title="Товары клиента">
            <Button 
              type="text" 
              icon={<ShoppingOutlined />} 
              onClick={() => showClientProductsModal(record)}
              size="small"
            />
          </Tooltip>
          <Tooltip title="Редактировать">
            <Button 
              type="text" 
              icon={<EditOutlined />} 
              onClick={() => showClientModal(record)}
              size="small"
            />
          </Tooltip>
          <Popconfirm
            title="Удалить клиента?"
            description={
              <div>
                <div>Это действие нельзя отменить</div>
                <div style={{ color: '#ff4d4f', marginTop: '8px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <ExclamationCircleOutlined />
                  <Text type="danger" style={{ fontSize: '12px' }}>
                    Клиентов с зарегистрированными изделиями удалять запрещено!
                  </Text>
                </div>
              </div>
            }
            onConfirm={() => handleDelete(record.id)}
            okText="Да"
            cancelText="Нет"
          >
            <Tooltip title="Удалить">
              <Button 
                type="text" 
                danger 
                icon={<DeleteOutlined />} 
                size="small"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  // Карточки клиента / изделия (переходы из тикетов поддержки)
  if (/^\/clients\/\d+/.test(location.pathname)) {
    return <ClientCard />;
  }
  if (/^\/products\/\d+/.test(location.pathname)) {
    return <ProductCard />;
  }

  return (
    <Layout style={{ minHeight: '100vh', maxWidth: '100vw', overflow: 'hidden' }}>
      <Navigation />
      
      <Content style={{ padding: '24px', background: '#f0f2f5' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          {/* Заголовок */}
          <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <Title level={2}>{activeTab === 'messages' ? 'Поддержка' : 'Клиенты'}</Title>
              <Text type="secondary">
                {activeTab === 'messages'
                  ? 'Очередь обращений и переписка с клиентами'
                  : 'Управление клиентской базой'}
              </Text>
            </div>
            {activeTab === 'clients' && (
              <Button 
                type="primary" 
                icon={<PlusOutlined />}
                size="large"
                onClick={() => showClientModal()}
              >
                Добавить клиента
              </Button>
            )}
          </div>

          {/* Вкладки */}
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              {
                key: 'messages',
                label: (
                  <span>
                    <InboxOutlined />
                    Поддержка
                    {newTicketsCount > 0 && (
                      <Badge count={newTicketsCount} style={{ marginLeft: 8 }} />
                    )}
                  </span>
                ),
                children: <MessagesTab />
              },
              {
                key: 'clients',
                label: (
                  <span>
                    <TeamOutlined />
                    Список клиентов
                  </span>
                ),
                children: (
                  <>
                    {/* Фильтры */}
                    <Card style={{ marginBottom: '24px' }}>
                      <Row gutter={[16, 16]} align="middle">
                        <Col xs={24} sm={12}>
                          <Input
                            placeholder="Поиск по имени, email, телефону или Telegram"
                            prefix={<SearchOutlined />}
                            value={searchText}
                            onChange={(e) => handleSearch(e.target.value)}
                            allowClear
                          />
                        </Col>
                        <Col xs={24} sm={8}>
                          <Select
                            placeholder="Статус"
                            value={selectedStatus}
                            onChange={handleStatusChange}
                            style={{ width: '100%' }}
                          >
                            <Option value="all">Все статусы</Option>
                            <Option value="active">Активные</Option>
                            <Option value="inactive">Неактивные</Option>
                          </Select>
                        </Col>
                        <Col xs={24} sm={4}>
                          <Text type="secondary">
                            Найдено: {filteredClients.length}
                          </Text>
                        </Col>
                      </Row>
                    </Card>

                    {/* Таблица клиентов */}
                    <Card>
                      <Table
                        columns={columns}
                        dataSource={filteredClients}
                        rowKey="id"
                        loading={loading}
                        pagination={{
                          pageSize: 10,
                          showSizeChanger: true,
                          showQuickJumper: true,
                          showTotal: (total, range) => 
                            `${range[0]}-${range[1]} из ${total} клиентов`
                        }}
                        scroll={{ x: 1000 }}
                      />
                    </Card>
                  </>
                )
              }
            ]}
          />
        </div>

        {/* Модальное окно клиента */}
        <Modal
          title={editingClient ? 'Редактирование клиента' : 'Новый клиент'}
          open={isModalVisible}
          onCancel={() => {
            setIsModalVisible(false);
            setIsEditing(false);
            setEditingClient(null);
            form.resetFields();
          }}
          footer={[
            <Button key="cancel" onClick={() => {
              setIsModalVisible(false);
              setIsEditing(false);
              setEditingClient(null);
              form.resetFields();
            }}>
              Отмена
            </Button>,
            <Button key="save" type="primary" icon={<SaveOutlined />} onClick={handleSave}>
              Сохранить
            </Button>
          ]}
          width={700}
        >
          <Form
            form={form}
            layout="vertical"
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="first_name"
                  label="Имя"
                  rules={[{ required: true, message: 'Введите имя!' }]}
                >
                  <Input placeholder="Имя" prefix={<UserOutlined />} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="last_name"
                  label="Фамилия"
                  rules={[{ required: true, message: 'Введите фамилию!' }]}
                >
                  <Input placeholder="Фамилия" prefix={<UserOutlined />} />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: 'Введите email!' },
                { type: 'email', message: 'Введите корректный email!' }
              ]}
            >
              <Input placeholder="Email" prefix={<MailOutlined />} />
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="phone"
                  label="Телефон"
                >
                  <Input placeholder="Телефон" prefix={<PhoneOutlined />} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="telegram_nickname"
                  label="Telegram"
                >
                  <Input placeholder="@username" prefix={<MessageOutlined />} />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="telegram_id"
              label="Telegram ID"
            >
              <Input placeholder="Telegram ID (число)" />
            </Form.Item>

            <Form.Item
              name="comment"
              label="Комментарий"
            >
              <TextArea rows={3} placeholder="Дополнительная информация о клиенте" />
            </Form.Item>

            <Form.Item
              name="is_active"
              label="Статус"
              valuePropName="checked"
            >
              <Switch checkedChildren="Активен" unCheckedChildren="Неактивен" />
            </Form.Item>

            {editingClient && (
              <div style={{ marginTop: '16px', padding: '16px', background: '#f5f5f5', borderRadius: '8px' }}>
                <Text strong>Дополнительная информация:</Text>
                <br />
                <Text type="secondary">ID: {editingClient.id}</Text>
                <br />
                <Text type="secondary">Создан: {new Date(editingClient.created_at).toLocaleDateString('ru-RU')}</Text>
                <br />
                <Text type="secondary">Обновлен: {new Date(editingClient.updated_at).toLocaleDateString('ru-RU')}</Text>
                {editingClient.token_expires_at && (
                  <>
                    <br />
                    <Text type="secondary">
                      Токен истекает: {new Date(editingClient.token_expires_at).toLocaleDateString('ru-RU')}
                    </Text>
                  </>
                )}
              </div>
            )}
          </Form>
        </Modal>

        {/* Модальное окно товаров клиента */}
        <Modal
          title={
            <div>
              <span>Товары клиента: {selectedClientForProducts ? `${selectedClientForProducts.first_name} ${selectedClientForProducts.last_name}` : ''}</span>
            </div>
          }
          open={isProductsModalVisible}
          onCancel={() => {
            setIsProductsModalVisible(false);
            setSelectedClientForProducts(null);
            setClientProducts([]);
          }}
          footer={[
            <Button key="close" onClick={() => {
              setIsProductsModalVisible(false);
              setSelectedClientForProducts(null);
              setClientProducts([]);
            }}>
              Закрыть
            </Button>
          ]}
          width={1200}
        >
          <Table
            columns={[
              {
                title: 'Фото',
                dataIndex: 'photoUrl',
                key: 'photo',
                width: 80,
                render: (url, record) =>
                  url ? (
                    <Image
                      src={url}
                      alt={record.name}
                      width={56}
                      height={56}
                      style={{ objectFit: 'cover', borderRadius: 6 }}
                    />
                  ) : (
                    <Avatar shape="square" size={56} icon={<ShoppingOutlined />} />
                  )
              },
              {
                title: 'Название',
                dataIndex: 'name',
                key: 'name',
                width: 200
              },
              {
                title: 'Серийный номер',
                dataIndex: 'serialNumber',
                key: 'serialNumber',
                width: 150
              },
              {
                title: 'Идентификационный номер',
                dataIndex: 'identificationNumber',
                key: 'identificationNumber',
                width: 180
              },
              {
                title: 'Размер',
                dataIndex: 'size',
                key: 'size',
                width: 100
              },
              {
                title: 'Цвет',
                dataIndex: 'color',
                key: 'color',
                width: 100
              },
              {
                title: 'Материал',
                dataIndex: 'material',
                key: 'material',
                width: 120
              },
              {
                title: 'Статус',
                dataIndex: 'status',
                key: 'status',
                width: 100,
                render: (status) => (
                  <Tag color={status === 'АКТИВНО' ? 'green' : 'default'}>
                    {status}
                  </Tag>
                )
              },
              {
                title: 'Дата получения',
                dataIndex: 'ownershipStartDate',
                key: 'ownershipStartDate',
                width: 150,
                render: (date) => date ? new Date(date).toLocaleDateString('ru-RU') : '-'
              },
              {
                title: 'Дата передачи',
                dataIndex: 'ownershipEndDate',
                key: 'ownershipEndDate',
                width: 150,
                render: (date) => date ? new Date(date).toLocaleDateString('ru-RU') : '-'
              }
            ]}
            dataSource={clientProducts}
            rowKey="id"
            loading={productsLoading}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Всего товаров: ${total}`
            }}
            scroll={{ x: 1000 }}
          />
        </Modal>
      </Content>
    </Layout>
  );
};

export default Clients;
